import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit as firestoreLimit,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Get social media connections for a user
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<Array>} - Connected social accounts
 */
export const getSocialConnections = async (userId) => {
  try {
    const q = query(
      collection(db, 'socialConnections'),
      where('userId', '==', userId),
      where('connected', '==', true)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting social connections:', error);
    throw new Error('Failed to load social connections');
  }
};

/**
 * Publish post immediately to selected platforms
 * @param {object} postData - Post data
 * @param {string} postData.postId - Post ID
 * @param {Array<string>} postData.platforms - Array of platform names
 * @param {string} postData.imageURL - Image URL
 * @param {string} postData.caption - Post caption
 * @param {Array<string>} postData.hashtags - Array of hashtags
 * @returns {Promise<object>} - Publishing results
 */
export const publishPostNow = async (postData) => {
  try {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    const publishNow = httpsCallable(functions, 'publishPostNow');
    const result = await publishNow({
      userId: auth.currentUser.uid,
      ...postData
    });

    return result.data;
  } catch (error) {
    console.error('Error publishing post:', error);

    if (error.code === 'functions/unauthenticated') {
      throw new Error('Please log in to publish posts');
    } else if (error.code === 'functions/permission-denied') {
      throw new Error('You do not have permission to publish');
    } else if (error.message) {
      throw new Error(error.message);
    }

    throw new Error('Failed to publish post. Please try again.');
  }
};

/**
 * Schedule post for future publishing
 * @param {object} postData - Post data
 * @param {string} postData.postId - Post ID
 * @param {Array<string>} postData.platforms - Array of platform names
 * @param {string} postData.scheduledFor - ISO 8601 datetime string
 * @param {string} postData.imageURL - Image URL
 * @param {string} postData.caption - Post caption
 * @param {Array<string>} postData.hashtags - Array of hashtags
 * @returns {Promise<object>} - Scheduling result
 */
export const schedulePost = async (postData) => {
  try {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    const schedulePostFunc = httpsCallable(functions, 'schedulePost');
    const result = await schedulePostFunc({
      userId: auth.currentUser.uid,
      ...postData
    });

    return result.data;
  } catch (error) {
    console.error('Error scheduling post:', error);

    if (error.code === 'functions/unauthenticated') {
      throw new Error('Please log in to schedule posts');
    } else if (error.code === 'functions/resource-exhausted') {
      throw new Error(error.message);
    } else if (error.message) {
      throw new Error(error.message);
    }

    throw new Error('Failed to schedule post. Please try again.');
  }
};

/**
 * Get scheduled posts for a user
 * @param {string} userId - Firebase Auth UID
 * @param {number} maxResults - Max number of posts to return
 * @returns {Promise<Array>} - Scheduled posts
 */
export const getScheduledPosts = async (userId, maxResults = 10) => {
  try {
    const q = query(
      collection(db, 'scheduledPosts'),
      where('userId', '==', userId),
      where('status', '==', 'pending'),
      orderBy('scheduledFor', 'asc'),
      firestoreLimit(maxResults)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting scheduled posts:', error);
    return [];
  }
};

/**
 * Cancel scheduled post
 * @param {string} scheduledPostId - Scheduled post ID
 * @returns {Promise<void>}
 */
export const cancelScheduledPost = async (scheduledPostId) => {
  try {
    const postRef = doc(db, 'scheduledPosts', scheduledPostId);
    await updateDoc(postRef, {
      status: 'cancelled',
      cancelledAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error cancelling scheduled post:', error);
    throw new Error('Failed to cancel scheduled post');
  }
};

/**
 * Save post to library
 * @param {string} userId - Firebase Auth UID
 * @param {object} postData - Post data
 * @param {string} workspaceId - Workspace ID (optional, for workspace isolation)
 * @returns {Promise<string>} - Document ID
 */
export const saveToLibrary = async (userId, postData, workspaceId = null) => {
  try {
    const docRef = await addDoc(collection(db, 'posts'), {
      userId,
      workspaceId,
      ...postData,
      published: false,
      archived: false,
      publishedTo: [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    return docRef.id;
  } catch (error) {
    console.error('Error saving to library:', error);
    throw new Error('Failed to save post to library');
  }
};

/**
 * Initiate OAuth flow for platform connection
 * @param {string} platform - Platform name (instagram, facebook, twitter)
 * @returns {string} - OAuth URL
 */
export const initiateOAuth = (platform) => {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    throw new Error('User must be authenticated');
  }

  // In production, this would call a Cloud Function
  // For now, return a placeholder URL
  const baseURL = process.env.REACT_APP_FUNCTIONS_URL || 'http://localhost:5001';
  return `${baseURL}/oauth/initiate?platform=${platform}&userId=${userId}`;
};

/**
 * Disconnect social media account
 * @param {string} platform - Platform name
 * @returns {Promise<void>}
 */
export const disconnectPlatform = async (platform) => {
  try {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    const disconnect = httpsCallable(functions, 'disconnectPlatform');
    await disconnect({
      userId: auth.currentUser.uid,
      platform
    });
  } catch (error) {
    console.error('Error disconnecting platform:', error);
    throw new Error(`Failed to disconnect ${platform}`);
  }
};

/**
 * Get all social connections for a user (including disconnected ones)
 * @param {string} userId - Firebase Auth UID
 * @param {object} filters - Filter options
 * @returns {Promise<Array>} - All social accounts
 */
export const getAllSocialConnections = async (userId, filters = {}) => {
  try {
    let q = query(
      collection(db, 'socialConnections'),
      where('userId', '==', userId)
    );

    // Apply filters
    if (filters.platform) {
      q = query(q, where('platform', '==', filters.platform));
    }

    if (filters.connected !== undefined) {
      q = query(q, where('connected', '==', filters.connected));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting all social connections:', error);
    throw new Error('Failed to load social connections');
  }
};

/**
 * Get single connection by ID
 * @param {string} connectionId - Connection document ID
 * @returns {Promise<object>} - Connection data
 */
export const getConnection = async (connectionId) => {
  try {
    const docRef = doc(db, 'socialConnections', connectionId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Connection not found');
    }

    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('Error getting connection:', error);
    throw new Error('Failed to load connection');
  }
};

/**
 * Delete a social connection
 * @param {string} connectionId - Connection document ID
 * @returns {Promise<void>}
 */
export const deleteConnection = async (connectionId) => {
  try {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    const deleteFunc = httpsCallable(functions, 'deleteConnection');
    await deleteFunc({ connectionId });
  } catch (error) {
    console.error('Error deleting connection:', error);
    throw new Error('Failed to delete connection');
  }
};

/**
 * Initiate Facebook OAuth flow
 * @returns {Promise<string>} - OAuth URL
 */
export const initiateFacebookOAuth = async () => {
  try {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    const initOAuth = httpsCallable(functions, 'initiateFacebookOAuth');
    const result = await initOAuth({ userId: auth.currentUser.uid });

    return result.data.authUrl;
  } catch (error) {
    console.error('Error initiating Facebook OAuth:', error);
    throw new Error('Failed to initiate Facebook connection');
  }
};

/**
 * Get Facebook pages for authenticated user
 * @returns {Promise<Array>} - List of Facebook pages
 */
export const getFacebookPages = async () => {
  try {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    const getPages = httpsCallable(functions, 'getFacebookPages');
    const result = await getPages({ userId: auth.currentUser.uid });

    return result.data.pages;
  } catch (error) {
    console.error('Error getting Facebook pages:', error);
    throw new Error('Failed to load Facebook pages');
  }
};

/**
 * Connect Facebook page
 * @param {object} pageData - Facebook page data
 * @returns {Promise<string>} - Connection ID
 */
export const connectFacebookPage = async (pageData) => {
  try {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    const connectPage = httpsCallable(functions, 'connectFacebookPage');
    const result = await connectPage({
      userId: auth.currentUser.uid,
      ...pageData
    });

    return result.data.connectionId;
  } catch (error) {
    console.error('Error connecting Facebook page:', error);
    throw new Error('Failed to connect Facebook page');
  }
};

/**
 * Reconnect/refresh a platform connection
 * @param {string} connectionId - Connection document ID
 * @returns {Promise<void>}
 */
export const reconnectPlatform = async (connectionId) => {
  try {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    const reconnect = httpsCallable(functions, 'reconnectPlatform');
    await reconnect({ connectionId });
  } catch (error) {
    console.error('Error reconnecting platform:', error);
    throw new Error('Failed to reconnect platform');
  }
};

/**
 * Handle Facebook OAuth callback
 * @param {string} userId - User ID
 * @param {string} code - Authorization code from Facebook
 * @param {string} state - State parameter for security
 * @returns {Promise<object>} - Result object
 */
export const handleFacebookCallback = async (userId, code, state) => {
  try {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    const callback = httpsCallable(functions, 'handleFacebookCallback');
    const result = await callback({ userId, code, state });

    return result.data;
  } catch (error) {
    console.error('Error handling Facebook callback:', error);
    throw new Error(error.message || 'Failed to complete Facebook connection');
  }
};
